// ════════════════════════════════════════════════════════════════════════════
//  pages/Fiscal/CTeOS.tsx
//  Listagem paginada de CT-e OS com emissão, cancelamento e carta de correção
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { FileText, XCircle, Search, ChevronLeft, ChevronRight, RefreshCw, Plus, Printer, PenLine } from 'lucide-react';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { useCTeOS } from '@/features/fiscal/transporte/hooks/useCTe';
import { cteOsService } from '@/features/fiscal/transporte/services/cteService';
import { statusCTeDeCStat } from '@/features/fiscal/transporte/types';
import type { StatusCTe, CTeResumoDto, EmitirCTeOsRequest, CancelarCTeRequest, CartaCorrecaoCTeRequest } from '@/features/fiscal/transporte/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

const PAGE_SIZES = [10, 20, 50] as const;

const statusConfig: Record<StatusCTe, { label: string; className: string }> = {
  autorizado: { label: 'Autorizado',   className: 'bg-green-500/10 text-green-600'   },
  cancelado:  { label: 'Cancelado',    className: 'bg-red-500/10 text-red-600'       },
  rejeitado:  { label: 'Rejeitado',    className: 'bg-orange-500/10 text-orange-600' },
  digitacao:  { label: 'Em digitação', className: 'bg-yellow-500/10 text-yellow-600' },
};

const chaveResumida = (chave: string | null) =>
  chave ? `...${chave.slice(-12)}` : '—';

const FORM_VAZIO_OS: EmitirCTeOsRequest = {
  serie: '1', natureza: '', cfop: '', modal: 1, tomador: 0,
  dtEmissao: new Date().toISOString().slice(0, 10),
  contratanteId: undefined,
  servicoDescricao: '', servicoValor: 0,
  cClassTrib: '', aliqCbs: 0, aliqIbs: 0,
  valorFrete: 0, veiculoId: undefined, motoristaNome: '',
};

const CTeOS = () => {
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [busca,         setBusca]         = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  // Abre nos conhecimentos de hoje, mais recentes primeiro.
  const periodo   = usePeriodo('hoje', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero'>('emissao', true, () => setPagina(1));

  // A busca vai ao servidor: espera a digitação parar.
  const [buscaEnviada, setBuscaEnviada] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setBuscaEnviada(busca.trim()), 400);
    return () => clearTimeout(t);
  }, [busca]);

  // Dialogs
  const [emitindoOpen,  setEmitindoOpen]  = useState(false);
  const [cancelandoDoc, setCancelandoDoc] = useState<CTeResumoDto | null>(null);
  const [cartaDoc,      setCartaDoc]      = useState<CTeResumoDto | null>(null);

  // Emissão form
  const [form, setForm] = useState<EmitirCTeOsRequest>(FORM_VAZIO_OS);

  // Cancelamento
  const [justificativa, setJustificativa] = useState('');

  // Carta de correção
  const [cartaCampo, setCartaCampo] = useState('');
  const [cartaTexto, setCartaTexto] = useState('');

  const { docs, total, totalPaginas, contagem, isLoading, isFetching, emitir, cancelar, cartaCorrecao } =
    useCTeOS(pagina, tamanhoPagina, {
      busca: buscaEnviada,
      situacao: statusFiltro,
      ...periodo.intervalo,
      ordenarPor: ordenacao.ordenarPor,
      decrescente: ordenacao.decrescente,
    });

  // Busca, situação e período já vêm filtrados do servidor.
  const docsFiltrados = docs;

  // Cartões: contagem do servidor para o período/busca inteiro (não só a página).
  const totalAutorizados = contagem?.autorizados ?? 0;
  const totalCancelados  = contagem?.cancelados  ?? 0;
  const totalRejeitados  = contagem?.rejeitados  ?? 0;
  const totalDigitacao   = contagem?.digitacao   ?? 0;

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  const setF = <K extends keyof EmitirCTeOsRequest>(k: K, v: EmitirCTeOsRequest[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const handleEmitir = async () => {
    await emitir.mutateAsync(form);
    setEmitindoOpen(false);
    setForm(FORM_VAZIO_OS);
  };

  const handleCancelar = () => {
    if (!cancelandoDoc || justificativa.trim().length < 15) return;
    cancelar.mutate({ id: cancelandoDoc.id, req: { justificativa } });
    setCancelandoDoc(null);
    setJustificativa('');
  };

  const handleCartaCorrecao = () => {
    if (!cartaDoc || !cartaCampo || !cartaTexto) return;
    cartaCorrecao.mutate({ id: cartaDoc.id, req: { campo: cartaCampo, textoCorrecao: cartaTexto } });
    setCartaDoc(null);
    setCartaCampo('');
    setCartaTexto('');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">CT-e OS</h1>
          <p className="text-muted-foreground mt-1">Conhecimento de Transporte Eletrônico de Outras Serviços</p>
        </div>
        <Button className="gap-2" onClick={() => { setForm(FORM_VAZIO_OS); setEmitindoOpen(true); }}>
          <Plus className="h-4 w-4" />
          Emitir CT-e OS
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Autorizados',  value: totalAutorizados, color: 'text-green-500'  },
          { label: 'Em digitação', value: totalDigitacao,   color: 'text-yellow-500' },
          { label: 'Cancelados',   value: totalCancelados,  color: 'text-red-500'    },
          { label: 'Rejeitados',   value: totalRejeitados,  color: 'text-orange-500' },
        ].map(({ label, value, color }) => (
          <Card key={label}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${color}`}>{value}</div>
              <p className="text-xs text-muted-foreground mt-1">No período filtrado</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
            <CardTitle className="whitespace-nowrap">Lista de CT-e OS</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Número, chave ou tomador..."
                  value={busca}
                  onChange={(e) => { setBusca(e.target.value); setPagina(1); }}
                  className="pl-9"
                />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-44">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os status</SelectItem>
                  <SelectItem value="autorizado">Autorizado</SelectItem>
                  <SelectItem value="cancelado">Cancelado</SelectItem>
                  <SelectItem value="rejeitado">Rejeitado</SelectItem>
                  <SelectItem value="digitacao">Em digitação</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Número</CabecalhoOrdenavel>
                  <TableHead>Série</TableHead>
                  <TableHead>Chave de Acesso</TableHead>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                  {/* No CT-e OS a API devolve o TOMADOR em remetenteNome. */}
                  <TableHead>Tomador</TableHead>
                  <TableHead className="text-right">Valor Prestação</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Carregando...</TableCell>
                  </TableRow>
                ) : docsFiltrados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {periodo.periodo === 'hoje' ? 'Nenhum CT-e OS emitido hoje — mude o período para ver os anteriores'
                        : 'Nenhum CT-e OS encontrado'}
                    </TableCell>
                  </TableRow>
                ) : (
                  docsFiltrados.map((doc) => {
                    const status = statusCTeDeCStat(doc.situacao, doc.isCancelado);
                    const cfg    = statusConfig[status];
                    return (
                      <TableRow key={doc.id}>
                        <TableCell className="font-medium">{String(doc.numero).padStart(6, '0')}</TableCell>
                        <TableCell>{doc.serie ?? '—'}</TableCell>
                        <TableCell className="text-xs font-mono text-muted-foreground" title={doc.chave ?? undefined}>
                          {chaveResumida(doc.chave)}
                        </TableCell>
                        <TableCell>{new Date(doc.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell className="max-w-[160px] truncate">{doc.remetenteNome ?? '—'}</TableCell>
                        <TableCell className="text-right">
                          {doc.valorPrestacao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>{cfg.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7"
                              onClick={() => window.open('/api/v1/fiscal/cte-os/' + doc.id + '/dacte', '_blank')}
                              title="DACTE">
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                            {status === 'autorizado' && (
                              <>
                                <Button size="icon" variant="ghost" className="h-7 w-7"
                                  onClick={() => { setCartaDoc(doc); setCartaCampo(''); setCartaTexto(''); }}
                                  title="Carta de Correção">
                                  <PenLine className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost"
                                  className="h-7 w-7 text-destructive hover:text-destructive"
                                  onClick={() => { setCancelandoDoc(doc); setJustificativa(''); }}
                                  title="Cancelar CT-e OS">
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Paginação */}
          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0
                ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={(v) => { setTamanhoPagina(Number(v)); setPagina(1); }}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)          page = i + 1;
                  else if (pagina <= 3)                 page = i + 1;
                  else if (pagina >= totalPaginas - 2)  page = totalPaginas - 4 + i;
                  else                                  page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dialog: Emitir CT-e OS */}
      <Dialog open={emitindoOpen} onOpenChange={(o) => { if (!o) setEmitindoOpen(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Emitir CT-e OS</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="identificacao">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="identificacao">Identificação</TabsTrigger>
              <TabsTrigger value="partes">Partes</TabsTrigger>
              <TabsTrigger value="servico">Serviço</TabsTrigger>
              <TabsTrigger value="impostos">Impostos</TabsTrigger>
            </TabsList>

            <TabsContent value="identificacao" className="space-y-3 pt-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Série *</Label>
                  <Input value={form.serie} onChange={(e) => setF('serie', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label>CFOP *</Label>
                  <Input value={form.cfop} onChange={(e) => setF('cfop', e.target.value)} placeholder="Ex: 6932" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Natureza da Operação *</Label>
                <Input value={form.natureza} onChange={(e) => setF('natureza', e.target.value)} placeholder="Ex: Prestação de outras serviços" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Tomador *</Label>
                  <Select value={String(form.tomador)} onValueChange={(v) => setF('tomador', Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 — Remetente</SelectItem>
                      <SelectItem value="1">1 — Expedidor</SelectItem>
                      <SelectItem value="2">2 — Recebedor</SelectItem>
                      <SelectItem value="3">3 — Destinatário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Modal *</Label>
                  <Select value={String(form.modal)} onValueChange={(v) => setF('modal', Number(v))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 — Rodoviário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Data de Emissão *</Label>
                <Input type="date" value={form.dtEmissao} onChange={(e) => setF('dtEmissao', e.target.value)} />
              </div>
            </TabsContent>

            <TabsContent value="partes" className="space-y-3 pt-3">
              <div className="space-y-1">
                <Label>ID Contratante</Label>
                <Input type="number" placeholder="ID do contratante cadastrado"
                  value={form.contratanteId ?? ''}
                  onChange={(e) => setF('contratanteId', e.target.value ? Number(e.target.value) : undefined)} />
              </div>
            </TabsContent>

            <TabsContent value="servico" className="space-y-3 pt-3">
              <div className="space-y-1">
                <Label>Descrição do Serviço *</Label>
                <Input value={form.servicoDescricao} onChange={(e) => setF('servicoDescricao', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Valor do Serviço (R$) *</Label>
                <Input type="number" min={0} step={0.01} value={form.servicoValor}
                  onChange={(e) => setF('servicoValor', Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label>Valor do Frete (R$)</Label>
                <Input type="number" min={0} step={0.01} value={form.valorFrete}
                  onChange={(e) => setF('valorFrete', Number(e.target.value))} />
              </div>
              <div className="space-y-1">
                <Label>ID Veículo</Label>
                <Input type="number" placeholder="ID do veículo cadastrado"
                  value={form.veiculoId ?? ''}
                  onChange={(e) => setF('veiculoId', e.target.value ? Number(e.target.value) : undefined)} />
              </div>
              <div className="space-y-1">
                <Label>Nome do Motorista</Label>
                <Input value={form.motoristaNome ?? ''} onChange={(e) => setF('motoristaNome', e.target.value)} />
              </div>
            </TabsContent>

            <TabsContent value="impostos" className="space-y-3 pt-3">
              <div className="space-y-1">
                <Label>Classe Tributária (cClassTrib) *</Label>
                <Input value={form.cClassTrib} onChange={(e) => setF('cClassTrib', e.target.value)} placeholder="Ex: 01" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Alíquota CBS (%)</Label>
                  <Input type="number" min={0} step={0.01} value={form.aliqCbs}
                    onChange={(e) => setF('aliqCbs', Number(e.target.value))} />
                </div>
                <div className="space-y-1">
                  <Label>Alíquota IBS (%)</Label>
                  <Input type="number" min={0} step={0.01} value={form.aliqIbs}
                    onChange={(e) => setF('aliqIbs', Number(e.target.value))} />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setEmitindoOpen(false)}>Cancelar</Button>
            <Button onClick={handleEmitir} disabled={emitir.isPending}>
              {emitir.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Emitindo...</>
              ) : (
                <><FileText className="h-4 w-4 mr-2" />Emitir CT-e OS</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Cancelar */}
      <Dialog open={!!cancelandoDoc} onOpenChange={(o) => { if (!o) setCancelandoDoc(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar CT-e OS {cancelandoDoc ? String(cancelandoDoc.numero).padStart(6, '0') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Informe a justificativa do cancelamento (mínimo 15 caracteres). Esta ação será enviada à SEFAZ.
            </p>
            <div className="space-y-1">
              <Label>Justificativa *</Label>
              <Textarea rows={4} placeholder="Descreva o motivo do cancelamento..."
                value={justificativa} onChange={(e) => setJustificativa(e.target.value)} />
              <p className="text-xs text-muted-foreground text-right">{justificativa.length} / 255 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelandoDoc(null)}>Fechar</Button>
            <Button variant="destructive" onClick={handleCancelar}
              disabled={cancelar.isPending || justificativa.trim().length < 15}>
              {cancelar.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cancelando...</>
              ) : (
                <><XCircle className="h-4 w-4 mr-2" />Confirmar Cancelamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Carta de Correção */}
      <Dialog open={!!cartaDoc} onOpenChange={(o) => { if (!o) setCartaDoc(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Carta de Correção — CT-e OS {cartaDoc ? String(cartaDoc.numero).padStart(6, '0') : ''}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Campo a corrigir *</Label>
              <Input value={cartaCampo} onChange={(e) => setCartaCampo(e.target.value)} placeholder="Ex: emit/xNome" />
            </div>
            <div className="space-y-1">
              <Label>Texto da correção *</Label>
              <Textarea rows={4} value={cartaTexto} onChange={(e) => setCartaTexto(e.target.value)}
                placeholder="Descreva a correção a ser aplicada..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCartaDoc(null)}>Fechar</Button>
            <Button onClick={handleCartaCorrecao}
              disabled={cartaCorrecao.isPending || !cartaCampo || !cartaTexto}>
              {cartaCorrecao.isPending ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Enviando...</>
              ) : (
                <><PenLine className="h-4 w-4 mr-2" />Enviar Carta de Correção</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CTeOS;
